import { useCallback, useEffect, useState } from 'react';
import {
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  $getSelection,
  $isRangeSelection,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  ElementFormatType,
  $isElementNode,
  $createParagraphNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  $isRootOrShadowRoot,
  $isTextNode
} from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $isParentElementRTL,
  $getSelectionStyleValueForProperty,
  $patchStyleText,
  $setBlocksType
} from '@lexical/selection';
import {
  $createHeadingNode,
  $createQuoteNode,
  HeadingTagType,
  $isHeadingNode,
  $isQuoteNode
} from '@lexical/rich-text';
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode
} from '@lexical/list';
import { $createCodeNode, $isCodeNode, CODE_LANGUAGE_MAP } from '@lexical/code';
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  $getNearestBlockElementAncestorOrThrow,
  mergeRegister
} from '@lexical/utils';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { sanitizeUrl } from '../../utils/url';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import { $isTableSelection } from '@lexical/table';

export interface EditorStateHook {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrikethrough: boolean;
  isSubscript: boolean;
  isSuperscript: boolean;
  isCode: boolean;
  isLink: boolean;

  blockType: string;
  codeLanguage: string;

  isRTL: boolean;
  elementFormat: ElementFormatType;

  fontSize: string;
  fontColor: string;
  bgColor: string;
  fontFamily: string;

  canUndo: boolean;
  canRedo: boolean;
  isEditable: boolean;

  toggleBold: () => void;
  toggleItalic: () => void;
  toggleUnderline: () => void;
  toggleStrikethrough: () => void;
  toggleSubscript: () => void;
  toggleSuperscript: () => void;
  toggleCode: () => void;
  toggleLink: (linkUrl?: string) => void;
  setFontColor: (color: string) => void;
  formatHeading: (headingSize: HeadingTagType) => void;
  formatList: (type: 'bullet' | 'number' | 'check') => void;
  formatParagraph: () => void;
  formatQuote: () => void;
  formatCode: () => void;
  setCodeLanguage: (language: string) => void;

  applyStyleText: (styles: Record<string, string>, skipHistoryStack?: boolean) => void;
  applyCodeLanguage: (lang: string) => void;
  clearFormatting: () => void;

  setAlignment: (format: ElementFormatType) => void;
  indent: () => void;
  outdent: () => void;

  undo: () => void;
  redo: () => void;
}

export const useEditorState = (editor: LexicalEditor): EditorStateHook => {
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isLink, setIsLink] = useState(false);

  const [blockType, setBlockType] = useState('paragraph');
  const [codeLanguage, setCodeLanguage] = useState('');

  const [isRTL, setIsRTL] = useState(false);
  const [elementFormat, setElementFormat] = useState<ElementFormatType>('left');

  const [fontSize, setFontSize] = useState<string>('18px');
  const [fontColor, setFontColor] = useState<string>('#1B1B1B');
  const [bgColor, setBgColor] = useState<string>('#fff');
  const [fontFamily, setFontFamily] = useState<string>('sans-serif');

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());

  const updateToolbar = useCallback(() => {
    editor.read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        setIsBold(selection.hasFormat('bold'));
        setIsItalic(selection.hasFormat('italic'));
        setIsUnderline(selection.hasFormat('underline'));
        setIsStrikethrough(selection.hasFormat('strikethrough'));
        setIsSubscript(selection.hasFormat('subscript'));
        setIsSuperscript(selection.hasFormat('superscript'));
        setIsCode(selection.hasFormat('code'));
        setIsRTL($isParentElementRTL(selection));

        const node = getSelectedNode(selection);
        const parent = node.getParent();
        setIsLink($isLinkNode(parent) || $isLinkNode(node));

        setFontColor($getSelectionStyleValueForProperty(selection, 'color', '#1B1B1B'));
        setBgColor($getSelectionStyleValueForProperty(selection, 'background-color', '#fff'));
        setFontFamily($getSelectionStyleValueForProperty(selection, 'font-family', 'sans-serif'));
        setFontSize($getSelectionStyleValueForProperty(selection, 'font-size', '18px'));

        const anchorNode = selection.anchor.getNode();
        const element = $findMatchingParent(anchorNode, (e) => {
          const parent = e.getParent();
          return parent !== null && $isRootOrShadowRoot(parent);
        });

        if (element !== null) {
          if ($isListNode(element)) {
            const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
            const type = parentList ? parentList.getListType() : element.getListType();
            setBlockType(type);
          } else {
            const type = $isHeadingNode(element) ? element.getTag() : element.getType();
            setBlockType(type);

            if ($isCodeNode(element)) {
              const language = element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
              setCodeLanguage(language ? CODE_LANGUAGE_MAP[language] || language : '');
            }
          }

          if ($isElementNode(element)) {
            setElementFormat(element.getFormatType() || 'left');
          }
        }
      }
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor, updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener((editable) => {
        setIsEditable(editable);
      }),
      editor.registerUpdateListener(() => {
        updateToolbar();
      }),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      )
    );
  }, [editor, updateToolbar]);

  const toggleBold = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  }, [editor]);

  const toggleItalic = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  }, [editor]);

  const toggleUnderline = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
  }, [editor]);

  const toggleStrikethrough = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
  }, [editor]);

  const toggleSubscript = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
  }, [editor]);

  const toggleSuperscript = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
  }, [editor]);

  const toggleCode = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
  }, [editor]);

  const toggleLink = useCallback(
    (linkUrl?: string) => {
      if (!isLink) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(linkUrl || 'https://'));
      } else {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }
    },
    [editor, isLink]
  );

  const formatHeading = useCallback(
    (headingSize: HeadingTagType) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.getNodes().forEach((node) => {
            if ($isTextNode(node)) {
              node.setStyle('');
            }
          });
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      });
    },
    [editor]
  );

  const formatParagraph = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  }, [editor]);

  const formatList = useCallback(
    (type: 'bullet' | 'number' | 'check') => {
      const commandMap = {
        bullet: INSERT_UNORDERED_LIST_COMMAND,
        number: INSERT_ORDERED_LIST_COMMAND,
        check: INSERT_CHECK_LIST_COMMAND
      };
      editor.dispatchCommand(commandMap[type], undefined);
    },
    [editor]
  );

  const formatQuote = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  }, [editor]);

  const formatCode = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (selection !== null) {
        if (selection.isCollapsed()) {
          $setBlocksType(selection, () => $createCodeNode());
        } else {
          const textContent = selection.getTextContent();
          const codeNode = $createCodeNode();
          selection.insertNodes([codeNode]);
          selection.insertRawText(textContent);
        }
      }
    });
  }, [editor]);

  const applyCodeLanguage = useCallback(
    (value: string) => {
      editor.update(() => {
        const selection = $getSelection();
        const parentNodes = selection
          .getNodes()
          .map((node) => node.getParents())
          .flat();
        for (const node of parentNodes) {
          if ($isCodeNode(node)) {
            setCodeLanguage(value);
            node.setLanguage(value);
          }
        }
      });
    },
    [editor]
  );

  const applyStyleText = useCallback(
    (styles: Record<string, string>, skipHistoryStack?: boolean) => {
      editor.update(
        () => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: 'historic' } : {}
      );
    },
    [editor]
  );

  const clearFormatting = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || $isTableSelection(selection)) {
        const anchor = selection.anchor;
        const focus = selection.focus;
        const nodes = selection.getNodes();
        const extractedNodes = selection.extract();

        if (anchor.key === focus.key && anchor.offset === focus.offset) {
          return;
        }

        nodes.forEach((node, idx) => {
          if ($isTextNode(node)) {
            let textNode = node;
            if (idx === 0 && anchor.offset !== 0) {
              textNode = textNode.splitText(anchor.offset)[1] || textNode;
            }
            if (idx === nodes.length - 1) {
              textNode = textNode.splitText(focus.offset)[0] || textNode;
            }
            const extractedTextNode = extractedNodes[0];
            if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
              textNode = extractedTextNode;
            }

            if (textNode.__style !== '') {
              textNode.setStyle('');
            }
            if (textNode.__format !== 0) {
              textNode.setFormat(0);
              $getNearestBlockElementAncestorOrThrow(textNode).setFormat('');
            }
            node = textNode;
          } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
            node.replace($createParagraphNode(), true);
          } else if ($isDecoratorBlockNode(node)) {
            node.setFormat('');
          }
        });
      }
    });
  }, [editor]);

  const setAlignment = useCallback(
    (format: ElementFormatType) => {
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format);
    },
    [editor]
  );

  const indent = useCallback(() => {
    editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
  }, [editor]);

  const outdent = useCallback(() => {
    editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
  }, [editor]);

  const undo = useCallback(() => {
    editor.dispatchCommand(UNDO_COMMAND, undefined);
  }, [editor]);

  const redo = useCallback(() => {
    editor.dispatchCommand(REDO_COMMAND, undefined);
  }, [editor]);

  return {
    isBold,
    isItalic,
    isUnderline,
    isStrikethrough,
    isSubscript,
    isSuperscript,
    isCode,
    isLink,
    isRTL,
    elementFormat,
    fontSize,
    fontColor,
    bgColor,
    fontFamily,
    blockType,
    codeLanguage,
    canUndo,
    canRedo,
    isEditable,
    setFontColor,
    toggleBold,
    toggleItalic,
    toggleUnderline,
    toggleStrikethrough,
    toggleSubscript,
    toggleSuperscript,
    toggleCode,
    toggleLink,
    formatHeading,
    formatList,
    formatParagraph,
    formatQuote,
    formatCode,
    setCodeLanguage,
    applyCodeLanguage,
    applyStyleText,
    clearFormatting,
    setAlignment,
    indent,
    outdent,
    undo,
    redo
  };
};
